/**
 * Atajos globales de `/admin`: qué hace cada combinación según la configuración
 * del Agente CEO (`ia_agentes.atajo_teclado`) y qué pantalla está montada.
 * Lógica pura con tests en `__tests__/atajosTeclado.test.ts`.
 *
 * Un solo listener (en `BuscadorMenus`, `keydown` en `window` en fase de captura,
 * con preventDefault + stopImmediatePropagation) decide; las pantallas que usan
 * un atajo solo se registran como "dueñas" y reciben la acción. La tecla se
 * reconoce por `e.code` (tecla física): en Mac ⌥K da `key` = "˚" y en otras
 * distribuciones `key` cambia. Cancelar el keydown también cancela el
 * keypress/beforeinput que escribiría ese carácter, así que no hacen falta más
 * listeners.
 *
 * ─── Qué combinación puede ganarle al navegador ────────────────────────────────
 * Ctrl/⌘+K (buscador):
 *   Chrome, Edge y Firefox (Windows, Linux, Mac) se lo dejan a la página si se
 *   cancela el keydown; si no, Chrome y Edge enfocan la barra de búsqueda y
 *   Firefox su búsqueda web. Safari macOS: ⌘K llega a la página. Ninguno lo
 *   recibe si el foco está en la barra de direcciones o en la interfaz del
 *   navegador: para eso queda "/" (fuera de inputs), como en GitHub.
 * Ctrl/⌘+Shift+K (descartado para el Agente CEO):
 *   Firefox abre la Consola web y la página no puede impedirlo; Edge (Windows)
 *   duplica la pestaña; Safari puede reservar ⌘⇧K. No se garantiza.
 * Alt+K / ⌥K (Agente CEO):
 *   Sin uso en Chrome, Edge, Firefox ni Safari (los menús de Firefox con Alt usan
 *   otras letras). En Mac ⌥K escribe "˚": se detecta por `code` y se cancela.
 *   AltGr+K (Ctrl+Alt en Windows) no cuenta: se exige Alt sin Ctrl ni ⌘.
 * "/" (buscador, alterno): solo fuera de campos de texto; Firefox lo usa para
 *   la búsqueda rápida y se lo deja a la página si se cancela.
 */
import { esTeclaK, type ObjetivoAtajo, type TeclaAtajo } from "./buscadorMenus";

/** Combinaciones que se atienden. `mod` = Ctrl en Windows/Linux, ⌘ en Mac. */
export type Combinacion = "mod+k" | "alt+k" | "slash";

/**
 * Atajo vigente del Agente CEO, visto desde el front:
 * - `mod+k` / `alt+k`: lo que dice `ia_agentes_disponibles()`.
 * - `sin_config`: la columna no existe (migración sin aplicar) o la RPC falló:
 *   se conserva el comportamiento de antes (Ctrl/⌘+K abre el agente).
 * - `sin_acceso`: la columna existe pero este usuario no tiene el agente.
 */
export type EstadoAtajoCeo = "mod+k" | "alt+k" | "sin_config" | "sin_acceso";

/** Pantallas que usan un atajo para lo suyo. */
export type DuenoAtajo = "agenteCeo" | "panorama";

export type OpcionSelector = "agenteCeo" | "buscador" | "cambiarAtajoCeo";

export type AccionAtajo =
  /** No es nuestro: no se intercepta y el navegador hace lo suyo. */
  | { tipo: "ninguna" }
  | { tipo: "buscador" }
  | { tipo: "dueno"; dueno: DuenoAtajo }
  | { tipo: "selector"; opciones: OpcionSelector[] };

export interface ContextoAtajo {
  atajoCeo: EstadoAtajoCeo;
  /** El Agente CEO está montado (Portal Alta Dirección, fuera de su página propia). */
  agenteCeoMontado: boolean;
  /** Panorama de Mercado está montado. */
  panoramaMontado: boolean;
  /** Rol 1: solo él ve la opción de cambiar el atajo (la RPC lo vuelve a validar). */
  esSuperAdmin: boolean;
}

const TIPOS_DE_TEXTO = new Set(["", "text", "search", "email", "url", "tel", "password", "number"]);

/** ¿El foco está en algo donde "/" se escribe? */
export function esCampoDeTexto(objetivo: ObjetivoAtajo | null | undefined): boolean {
  if (!objetivo) return false;
  if (objetivo.isContentEditable) return true;
  const tag = (objetivo.tagName ?? "").toUpperCase();
  if (tag === "TEXTAREA" || tag === "SELECT") return true;
  return tag === "INPUT" && TIPOS_DE_TEXTO.has((objetivo.type ?? "").toLowerCase());
}

/** Qué combinación es, o null. La tecla física (`code`) manda. */
export function combinacionDe(
  e: TeclaAtajo,
  esMac: boolean,
  objetivo: ObjetivoAtajo | null | undefined = null,
): Combinacion | null {
  if (esTeclaK(e) && !e.shiftKey) {
    const mod = esMac ? e.metaKey && !e.ctrlKey : e.ctrlKey && !e.metaKey;
    if (mod && !e.altKey) return "mod+k";
    if (e.altKey && !e.ctrlKey && !e.metaKey) return "alt+k";
    return null;
  }
  // "/" alterno: en distribuciones como la española va con Shift (Shift+7).
  if (e.key === "/" && !e.ctrlKey && !e.metaKey && !e.altKey && !esCampoDeTexto(objetivo)) return "slash";
  return null;
}

/**
 * Qué hacer con una combinación.
 *
 * Ctrl/⌘+K:
 * - Con el Agente CEO montado y su atajo aún en mod+k: selector entre el agente
 *   y el buscador (más "cambiar el atajo" para el Super Admin).
 * - Sin configuración en BD: abre el agente, como siempre.
 * - En cualquier otro caso: buscador de menús.
 *
 * Alt+K / ⌥K:
 * - Abre el Agente CEO si su atajo ya es alt+k.
 * - Si no, abre lo de Panorama de Mercado cuando está montado.
 * - Si no, no se intercepta.
 *
 * "/": buscador de menús (el llamador ya descartó campos de texto).
 */
export function resolverAtajo(combinacion: Combinacion | null, ctx: ContextoAtajo): AccionAtajo {
  if (!combinacion) return { tipo: "ninguna" };
  if (combinacion === "slash") return { tipo: "buscador" };

  if (combinacion === "mod+k") {
    if (ctx.agenteCeoMontado) {
      if (ctx.atajoCeo === "mod+k") {
        const opciones: OpcionSelector[] = ["agenteCeo", "buscador"];
        if (ctx.esSuperAdmin) opciones.push("cambiarAtajoCeo");
        return { tipo: "selector", opciones };
      }
      if (ctx.atajoCeo === "sin_config") return { tipo: "dueno", dueno: "agenteCeo" };
    }
    return { tipo: "buscador" };
  }

  if (ctx.agenteCeoMontado && ctx.atajoCeo === "alt+k") return { tipo: "dueno", dueno: "agenteCeo" };
  if (ctx.panoramaMontado) return { tipo: "dueno", dueno: "panorama" };
  return { tipo: "ninguna" };
}

/** Lee el estado desde las filas de `ia_agentes_disponibles()` (null o no-arreglo = falló). */
export function estadoAtajoCeoDesdeFilas(filas: unknown, codigoCeo = "bot-agente-alta-direccion"): EstadoAtajoCeo {
  if (!Array.isArray(filas)) return "sin_config";
  const conColumna = filas.some((f) => f && typeof f === "object" && "atajo_teclado" in f);
  if (!conColumna) return "sin_config";
  const ceo = filas.find((f) => f && typeof f === "object" && (f as { codigo?: unknown }).codigo === codigoCeo) as
    | { atajo_teclado?: unknown }
    | undefined;
  if (!ceo) return "sin_acceso";
  return ceo.atajo_teclado === "alt+k" ? "alt+k" : "mod+k";
}

/** ¿Mac o iOS? (ahí `mod` es ⌘ y Alt es ⌥). */
export function esMac(): boolean {
  if (typeof navigator === "undefined") return false;
  const nav = navigator as Navigator & { userAgentData?: { platform?: string } };
  return /mac|iphone|ipad|ipod/i.test(nav.userAgentData?.platform || nav.platform || nav.userAgent || "");
}

/** "Ctrl+K", "Alt+K", "⌘K", "⌥K" o "/". */
export function etiquetaCombinacion(c: Combinacion, esMac: boolean): string {
  if (c === "slash") return "/";
  if (esMac) return c === "alt+k" ? "⌥K" : "⌘K";
  return c === "alt+k" ? "Alt+K" : "Ctrl+K";
}

/** Atajo con el que hoy se abre el Agente CEO, para tooltips y textos. */
export function combinacionAgenteCeo(estado: EstadoAtajoCeo): Combinacion {
  return estado === "alt+k" ? "alt+k" : "mod+k";
}

// ---------------------------------------------------------------------------
// Dueños registrados (estado de módulo: un solo árbol de React por pestaña)
// ---------------------------------------------------------------------------

const duenos = new Map<DuenoAtajo, Array<() => void>>();

/**
 * La pantalla `dueno` atiende su acción mientras esté montada. Devuelve la
 * función que la quita. Si hay varias del mismo dueño, gana la última montada.
 */
export function registrarDuenoAtajo(dueno: DuenoAtajo, accion: () => void): () => void {
  const lista = duenos.get(dueno) ?? [];
  lista.push(accion);
  duenos.set(dueno, lista);
  let quitado = false;
  return () => {
    if (quitado) return;
    quitado = true;
    const actual = duenos.get(dueno) ?? [];
    const i = actual.lastIndexOf(accion);
    if (i >= 0) actual.splice(i, 1);
    if (actual.length === 0) duenos.delete(dueno);
  };
}

export const hayDuenoAtajo = (dueno: DuenoAtajo) => (duenos.get(dueno)?.length ?? 0) > 0;

/** Ejecuta la acción del dueño. false si no hay nadie registrado. */
export function ejecutarDuenoAtajo(dueno: DuenoAtajo): boolean {
  const lista = duenos.get(dueno);
  const accion = lista?.[lista.length - 1];
  if (!accion) return false;
  accion();
  return true;
}
